import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe, NgIf, NgFor } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ActivatedRoute, Router } from '@angular/router';

import { Note, Project, Status, NoteType, DataService } from '../../_service/data.service';
import { DataOfflineService } from '../../_service/data-offline.service';
import { DataRemoteService } from '../../_service/data-remote.service';
import { EnvironmentService } from '../../_service/sync/environment.service';
import { NavigationService } from '../../_service/navigation.service';
import { SyncCoordinatorService } from '../../_service/sync/sync-coordinator.service';
import { ButtonModule } from 'primeng/button';
import { ConflictTransferService } from '../../_service/conflict-transfer.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';

export interface noteSyncInfos {
  _id: string;
  c_missing: boolean; // is the note missing on CouchDB server?
  l_missing: boolean; // is the note missing on local storage?
  syncStatus?: string;
  syncTime?: string;
  outOfSyncFlag: boolean; // are the note versions different?
  c_rev?: string;
  l_rev?: string;
  c_Status: string; // couchDB status
  l_Status: string; // local status
  c_created_on: string;
  c_last_modified: string;
  l_created_on: string;
  l_last_modified: string;
  type: string;
  NoteType: string;
  Title: string;
  Project: number;
  ProjectName: string;
  Description: string;
  Comments: string;
  Keywords: string;
}

@Component({
  selector: 'sync-list',
  standalone: true,
  imports: [CommonModule, DatePipe, NgIf, NgFor, FormsModule, ButtonModule, RouterModule, ProgressSpinnerModule, TableModule],
  templateUrl: './sync-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SyncListComponent implements OnInit {
  offlineNotes: Note[] = [];
  onlineNotes: Note[] = [];
  noteSyncList: any[] = []; // Adjust type as necessary
  filteredSyncList: any[] = [];
  loading = false;
  error: string | null = null;

  // Filter and sort options
  filterSyncStatus: string = '';
  filterStatus: string = '';
  filterProjectName: string = '';
  filterOutOfSyncFlag: string = 'true';

  statusOptions: string[] = [];
  projectNameOptions: string[] = [];
  sortField: string = 'SyncStatus'; // default sort field
  sortDirection: 'asc' | 'desc' = 'asc';

  syncStatusOptions: string[] = [];

  constructor(
    private offline: DataOfflineService,
    private remote: DataRemoteService,
    private environment: EnvironmentService,
    private navigationService: NavigationService,
    private router: Router,
    private conflictTransfer: ConflictTransferService,
    private cdr: ChangeDetectorRef,
    private syncCoordinator: SyncCoordinatorService,
  ) {}

  ngOnInit(): void {
    console.log('SyncListComponent initialized');
    this.reload();
  }

  pageMenu(selectedItem: string): void {
    console.log('pageMenu: ', selectedItem);
    if (selectedItem == 'Home') {
      this.navigationService.clearHistory();
      this.router.navigate(['home']);
    } else if (selectedItem == 'Close') {
      this.navigationService.clearHistory();
      this.router.navigate(['home']);
    }
  }

  reload(): void {
    console.log('Reloading notes from IndexedDB...');
    this.loading = true;
    this.error = null;
    this.cdr.markForCheck();

    // Load offline notes
    this.offline
      .getAllNotes()
      .then((notes) => {
        this.offlineNotes = notes;
        console.log(`Loaded ${notes.length} notes from IndexedDB.`);
        this.cdr.markForCheck();
        // After offline notes are loaded, check online status and load online notes
        this.environment.online$.subscribe((isOnline) => {
          if (isOnline) {
            console.log('Online: Loading notes from CouchDB...');
            this.remote.getAllNotes().subscribe({
              next: (onlineNotes) => {
                this.onlineNotes = onlineNotes;
                console.log(`Loaded ${onlineNotes.length} notes from CouchDB.`);
                this.analyzeSyncStatus();
                this.loading = false;
                this.cdr.markForCheck();
              },
              error: (err) => {
                this.error = err?.message ?? 'Failed to load notes from CouchDB.';
                console.error('Error loading notes from CouchDB:', err);
                this.analyzeSyncStatus();
                this.loading = false;
                this.cdr.markForCheck();
              },
            });
          } else {
            console.log('Offline: Skipping CouchDB notes load.');
            this.analyzeSyncStatus();
            this.loading = false;
            this.cdr.markForCheck();
          }
        });
      })
      .catch((err) => {
        this.error = err?.message ?? 'Failed to load notes from IndexedDB.';
        console.error('Error loading notes from IndexedDB:', err);
        this.loading = false;
        this.cdr.markForCheck();
      });
  }

  analyzeSyncStatus(): void {
    const offlineMap = new Map(this.offlineNotes.map((note) => [note._id, note]));
    const onlineMap = new Map(this.onlineNotes.map((note) => [note._id, note]));

    const allIds = new Set([...offlineMap.keys(), ...onlineMap.keys()]);

    this.noteSyncList = [];
    const syncStatusSet = new Set<string>();
    const statusSet = new Set<string>();
    const projectNameSet = new Set<string>();
    for (const id of allIds) {
      const l_note = offlineMap.get(id);
      const c_note = onlineMap.get(id);
      const record: any = { _id: id };

      // Offline note fields
      if (!l_note) {
        record.l_missing = true;
      } else {
        record.l_missing = false;
        record.syncStatus = l_note.syncStatus ?? null;
        record.syncTime = l_note.syncTime ?? null;
        record.l_rev = l_note._rev ?? null;
        record.l_Status = l_note.Status ?? null;
        record.l_created_on = l_note.created_on ?? null;
        record.l_last_modified = l_note.last_modified ?? null;
        record.type = l_note.type ?? null;
        record.ProjectName = l_note.ProjectName ?? null;
        record.Title = l_note.Title ?? null;
        if (l_note.syncStatus) syncStatusSet.add(l_note.syncStatus);
        if (l_note.Status) statusSet.add(l_note.Status);
        if (l_note.ProjectName) projectNameSet.add(l_note.ProjectName);
      }

      // Online note fields
      if (!c_note) {
        record.c_missing = true;
      } else {
        record.c_missing = false;
        record.c_rev = c_note._rev ?? null;
        record.c_Status = c_note.Status ?? null;
        record.c_created_on = c_note.created_on ?? null;
        record.c_last_modified = c_note.last_modified ?? null;
        record.type = c_note.type ?? record.type;
        record.ProjectName = c_note.ProjectName ?? record.ProjectName;
        if (c_note.syncStatus) syncStatusSet.add(c_note.syncStatus);
        if (c_note.Status) statusSet.add(c_note.Status);
        if (c_note.ProjectName) projectNameSet.add(c_note.ProjectName);
      }

      // Out of sync flag
      record.outOfSyncFlag = l_note && c_note ? l_note._rev !== c_note._rev : false;
      if (record.c_missing || record.l_missing) record.outOfSyncFlag = true;

      record.pushFailure = this.syncCoordinator.getPushFailureState(id);

      this.noteSyncList.push(record);
    }
    this.syncStatusOptions = Array.from(syncStatusSet);
    this.statusOptions = Array.from(statusSet);
    this.projectNameOptions = Array.from(projectNameSet);
    this.applyFiltersAndSort();
  }

  applyFiltersAndSort(): void {
    let filtered = this.noteSyncList;
    if (this.filterSyncStatus) {
      filtered = filtered.filter((n) => n.syncStatus === this.filterSyncStatus);
    }
    if (this.filterStatus) {
      filtered = filtered.filter((n) => n.l_Status === this.filterStatus || n.c_Status === this.filterStatus);
    }
    if (this.filterProjectName) {
      filtered = filtered.filter((n) => n.ProjectName === this.filterProjectName);
    }
    if (this.filterOutOfSyncFlag) {
      if (this.filterOutOfSyncFlag === 'true') {
        filtered = filtered.filter((n) => n.outOfSyncFlag === true);
      } else if (this.filterOutOfSyncFlag === 'false') {
        filtered = filtered.filter((n) => n.outOfSyncFlag === false);
      }
      // if 'any', do not filter
    }

    // Sorting
    filtered = filtered.sort((a, b) => {
      let valA, valB;
      switch (this.sortField) {
        case 'SyncStatus':
          valA = a.syncStatus || '';
          valB = b.syncStatus || '';
          break;
        case '_id':
          valA = a._id;
          valB = b._id;
          break;
        case 'SyncTime':
          valA = a.syncTime || '';
          valB = b.syncTime || '';
          break;
        case 'ModifiedDate':
          valA = a.l_last_modified || a.c_last_modified || '';
          valB = b.l_last_modified || b.c_last_modified || '';
          break;
        default:
          valA = a[this.sortField] || '';
          valB = b[this.sortField] || '';
      }
      if (valA < valB) return this.sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    this.filteredSyncList = filtered;
  }

  setFilterSyncStatus(status: string) {
    this.filterSyncStatus = status;
    this.applyFiltersAndSort();
  }
  setFilterStatus(status: string) {
    this.filterStatus = status;
    this.applyFiltersAndSort();
  }
  setFilterProjectName(name: string) {
    this.filterProjectName = name;
    this.applyFiltersAndSort();
  }
  setSortField(field: string) {
    this.sortField = field;
    this.applyFiltersAndSort();
  }
  setSortDirection(direction: 'asc' | 'desc') {
    this.sortDirection = direction;
    this.applyFiltersAndSort();
  }

  viewConflict(noteId: string): void {
    console.log('Viewing conflict for note ID: ', noteId);
    const offlineNote = this.offlineNotes.find((note) => note._id === noteId);
    const onlineNote = this.onlineNotes.find((note) => note._id === noteId);
    if (!offlineNote || !onlineNote) {
      console.error('Cannot view conflict: Note not found in both offline and online lists.');
      return;
    }
    this.conflictTransfer.setNotes(offlineNote, onlineNote);
    this.router.navigate(['/view-conflict']);
  }

  copyToClipboard(value: string | null | undefined): void {
    if (!value) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(value).catch((err) => {
        console.error('Failed to copy to clipboard:', err);
      });
    }
  }

  deleteRecord(type: string, id: string): void {
    console.log('TO DO: Deleting ', type, '  record: ', id);
  }

  formatPushRetry(rec: { pushFailure?: import('../../_service/sync/sync-coordinator.service').PushFailureState }): string {
    const failure = rec.pushFailure;
    if (!failure) return '';

    const msUntilRetry = Date.parse(failure.nextRetryAt) - Date.now();
    const minutesUntilRetry = Math.max(0, Math.ceil(msUntilRetry / 60_000));
    const retryText = minutesUntilRetry > 0 ? `retry in ${minutesUntilRetry}m` : 'retrying soon';

    return `${failure.failedAttempts}x failed — ${retryText}`;
  }
}
